import { useEffect, useState } from "react";
import { Eye, EyeOff, Monitor, Send, Smartphone, X } from "lucide-react";
import { api, eur } from "@/lib/api";

const SEG_BADGE = { a: "A", b: "B", c: "C", d: "D" };

export default function AdminArenaCampaign({ token }) {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [brevo, setBrevo] = useState(null);
  const [preview, setPreview] = useState(null); // {segment, subject, preheader, html}
  const [previewMode, setPreviewMode] = useState("desktop");
  const [testEmail, setTestEmail] = useState({});
  const [msg, setMsg] = useState({});
  const [busy, setBusy] = useState({});

  const load = () => {
    api.adminArenaConfig(token).then(setConfig).catch(() => {});
    api.adminArenaStats(token).then(setStats).catch(() => {});
    api.adminBrevoStats(token).then(setBrevo).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [token]);

  const say = (seg, text, isErr = false) => setMsg((m) => ({ ...m, [seg]: { text, isErr } }));

  const openPreview = async (segment, mode) => {
    setBusy((b) => ({ ...b, [`pv-${segment}`]: true }));
    try {
      const p = await api.adminArenaPreview(token, segment);
      setPreview({ segment, ...p });
      setPreviewMode(mode);
    } catch {
      say(segment, "Anteprima non disponibile", true);
    } finally {
      setBusy((b) => ({ ...b, [`pv-${segment}`]: false }));
    }
  };

  const sendTest = async (segment) => {
    const email = (testEmail[segment] || "").trim();
    if (!email) return say(segment, "Inserisci un indirizzo email", true);
    setBusy((b) => ({ ...b, [`t-${segment}`]: true }));
    try {
      await api.adminArenaTest(token, segment, email);
      say(segment, `Email di test inviata a ${email}`);
    } catch (e) {
      say(segment, e.response?.data?.detail || "Invio test fallito", true);
    } finally {
      setBusy((b) => ({ ...b, [`t-${segment}`]: false }));
    }
  };

  const sendCampaign = async (segment, recipients) => {
    if (!window.confirm(`Confermi l'invio della campagna al ${SEG_BADGE[segment] === "A" ? "Segmento A" : SEG_BADGE[segment] === "B" ? "Segmento B" : "Segmento C"} (${recipients} destinatari)? L'operazione non si può annullare.`)) return;
    setBusy((b) => ({ ...b, [`s-${segment}`]: true }));
    try {
      const r = await api.adminArenaSend(token, segment);
      say(segment, `Invio avviato: ${r.recipients} destinatari. L'invio procede in background (~1 email ogni 15s).`);
      setTimeout(load, 3000);
    } catch (e) {
      say(segment, e.response?.data?.detail || "Invio fallito", true);
    } finally {
      setBusy((b) => ({ ...b, [`s-${segment}`]: false }));
    }
  };

  if (!config) return <div data-testid="arena-loading" className="py-16 text-center text-sm text-navy/40">Caricamento campagna Arena…</div>;

  const statFor = (sid) => stats?.segments?.find((s) => s.segment === sid);

  return (
    <div data-testid="arena-campaign" className="mt-8 space-y-10">
      <div className="border border-navy/10 bg-white p-8">
        <p className="label-luxe text-navy/40">Campagna follow-up</p>
        <h2 className="mt-2 font-display text-3xl font-light text-navy">Arena Rapid ULTRA — 3+1 &amp; 5+1 ΔΩΡΟ</h2>
        <p className="mt-3 max-w-2xl text-sm font-light leading-relaxed text-navy/55">
          Tre segmenti calcolati dalla campagna pre-lancio ({config.previous_campaign}). Nessun invio è automatico:
          controlla l'anteprima, manda un test e approva manualmente ogni segmento.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {[config.offers["31"], config.offers["51"]].map((o) => (
            <div key={o.slug} data-testid={`arena-offer-${o.slug}`} className="border border-navy/10 p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/40">{o.slug}</p>
              <p className="mt-2 text-sm text-navy">
                Prezzo: <strong>{o.price != null ? eur(o.price) : "—"}</strong> · spedite {o.ships} confezioni ·{" "}
                {o.live ? <span className="text-green-700">pagina live</span> : <span className="text-red-700">NON pubblicata</span>}
              </p>
              <a href={o.url} target="_blank" rel="noreferrer" data-testid={`arena-offer-url-${o.slug}`} className="mt-2 block break-all text-xs text-gold-dark underline">
                {o.url}
              </a>
            </div>
          ))}
        </div>
      </div>

      {config.segments.map((s) => {
        const st = statFor(s.id);
        const m = msg[s.id];
        return (
          <div key={s.id} data-testid={`arena-segment-${s.id}`} className="border border-navy/10 bg-white p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="label-luxe text-navy/40">{s.name}</p>
                <p className="mt-2 font-display text-4xl font-light text-navy" data-testid={`arena-count-${s.id}`}>{s.recipients} <span className="text-base text-navy/40">destinatari</span></p>
              </div>
              {st?.in_progress && <span className="border border-gold px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-gold-dark">Invio in corso — {st.sent}/{st.recipients}</span>}
            </div>
            <div className="mt-5 space-y-1.5 text-sm text-navy/70">
              <p><span className="text-navy/40">Oggetto:</span> <span data-testid={`arena-subject-${s.id}`}>{s.subject}</span></p>
              <p><span className="text-navy/40">Preheader:</span> {s.preheader}</p>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button data-testid={`arena-preview-desktop-${s.id}`} onClick={() => openPreview(s.id, "desktop")} disabled={busy[`pv-${s.id}`]} className="btn-primary-luxe inline-flex items-center gap-2">
                <Monitor size={14} /> Anteprima desktop
              </button>
              <button data-testid={`arena-preview-mobile-${s.id}`} onClick={() => openPreview(s.id, "mobile")} disabled={busy[`pv-${s.id}`]} className="inline-flex items-center gap-2 border border-navy/15 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/60 transition-colors hover:border-gold">
                <Smartphone size={14} /> Mobile
              </button>
              <input
                data-testid={`arena-test-email-${s.id}`}
                type="email"
                placeholder="email di test"
                value={testEmail[s.id] || ""}
                onChange={(e) => setTestEmail((t) => ({ ...t, [s.id]: e.target.value }))}
                className="w-56 border border-navy/15 bg-white px-4 py-2.5 text-sm text-navy outline-none focus:border-gold"
              />
              <button data-testid={`arena-test-send-${s.id}`} onClick={() => sendTest(s.id)} disabled={busy[`t-${s.id}`]} className="inline-flex items-center gap-2 border border-navy/15 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/60 transition-colors hover:border-gold">
                <Send size={14} /> {busy[`t-${s.id}`] ? "Invio…" : "Invia test"}
              </button>
              <button data-testid={`arena-send-${s.id}`} onClick={() => sendCampaign(s.id, s.recipients)} disabled={busy[`s-${s.id}`] || s.recipients === 0} className="inline-flex items-center gap-2 border border-red-800 bg-red-800 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-red-900 disabled:opacity-40">
                {busy[`s-${s.id}`] ? "Avvio…" : `Invia campagna (${s.recipients})`}
              </button>
            </div>
            {m && <p data-testid={`arena-msg-${s.id}`} className={`mt-4 text-sm ${m.isErr ? "text-red-700" : "text-green-700"}`}>{m.text}</p>}
          </div>
        );
      })}

      {stats && (
        <div data-testid="arena-stats" className="border border-navy/10 bg-white p-8">
          <p className="label-luxe text-navy/40">Report campagna</p>
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-start text-xs">
              <thead>
                <tr className="border-b border-navy/10 text-navy/40">
                  {["Segmento", "Destinatari", "Inviate", "Aperture", "Tasso ap.", "Clic unici", "Tasso clic", "Clic 3+1", "Clic 5+1", "Clic prodotto", "Ordini", "Conv.", "Fatturato"].map((h) => (
                    <th key={h} className="px-3 py-3 text-start font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.segments.map((s) => (
                  <tr key={s.segment} data-testid={`arena-stats-row-${s.segment}`} className="border-b border-navy/5 text-navy/70">
                    <td className="px-3 py-3 font-semibold uppercase">{s.segment}</td>
                    <td className="px-3 py-3">{s.recipients}</td>
                    <td className="px-3 py-3">{s.sent}</td>
                    <td className="px-3 py-3">{s.unique_opens}</td>
                    <td className="px-3 py-3">{s.open_rate}%</td>
                    <td className="px-3 py-3">{s.unique_clicks}</td>
                    <td className="px-3 py-3">{s.click_rate}%</td>
                    <td className="px-3 py-3">{s.clicks_31}</td>
                    <td className="px-3 py-3">{s.clicks_51}</td>
                    <td className="px-3 py-3">{s.clicks_product}</td>
                    <td className="px-3 py-3">{s.purchases}</td>
                    <td className="px-3 py-3">{s.conversion_rate}%</td>
                    <td className="px-3 py-3">{eur(s.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="label-luxe mt-8 text-navy/40">Confronto offerte (ordini reali con bundle)</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {[["31", "Offerta 3+1"], ["51", "Offerta 5+1"]].map(([k, label]) => (
              <div key={k} data-testid={`arena-offer-stats-${k}`} className="border border-navy/10 p-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/40">{label}</p>
                <p className="mt-2 text-sm text-navy">Clic: <strong>{stats.offer_comparison[k].clicks}</strong> · Ordini: <strong>{stats.offer_comparison[k].orders}</strong> · Fatturato: <strong>{eur(stats.offer_comparison[k].revenue)}</strong></p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-navy/40">Le disiscrizioni non sono tracciate (nessun sistema di opt-out attivo). Gli ordini sono attribuiti per email cliente dal momento dell'invio del segmento.</p>
        </div>
      )}

      {brevo && (
        <div data-testid="brevo-stats" className="border border-navy/10 bg-white p-8">
          <p className="label-luxe text-navy/40">Account email (Brevo)</p>
          {!brevo.configured ? (
            <p className="mt-3 text-sm text-navy/55">
              Brevo non è ancora collegato. Quando incolli la chiave API, qui vedrai le statistiche reali del tuo account:
              consegne, bounce e spam (ultimi 30 giorni).
            </p>
          ) : brevo.error ? (
            <p className="mt-3 text-sm text-red-700">Errore Brevo: {brevo.error} — controlla la chiave API.</p>
          ) : (
            <div className="mt-5 grid gap-4 sm:grid-cols-4">
              {[
                ["Inviate", brevo.stats.sent],
                ["Consegnate", `${brevo.stats.delivered} (${brevo.stats.delivery_rate}%)`],
                ["Bounce", brevo.stats.hard_bounces + brevo.stats.soft_bounces],
                ["Spam", brevo.stats.spam],
              ].map(([label, value]) => (
                <div key={label} className="border border-navy/10 p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/40">{label}</p>
                  <p className="mt-2 font-display text-2xl font-light text-navy" data-testid={`brevo-${label.toLowerCase()}`}>{value}</p>
                </div>
              ))}
              <p className="text-xs text-navy/40 sm:col-span-4">Dati reali dal tuo account Brevo · {brevo.range.start} → {brevo.range.end}</p>
            </div>
          )}
        </div>
      )}

      {preview && (
        <div data-testid="arena-preview-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-navy/80 p-4" onClick={() => setPreview(null)}>
          <div className="flex max-h-full flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between gap-4">
              <div className="text-white">
                <p className="text-sm font-semibold" data-testid="arena-preview-subject">{preview.subject}</p>
                <p className="text-xs text-white/60">{preview.preheader}</p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="arena-preview-desktop-toggle" onClick={() => setPreviewMode("desktop")} className={`px-3 py-1.5 text-[11px] uppercase tracking-wider ${previewMode === "desktop" ? "bg-gold text-navy" : "bg-white/10 text-white"}`}><Monitor size={14} /></button>
                <button data-testid="arena-preview-mobile-toggle" onClick={() => setPreviewMode("mobile")} className={`px-3 py-1.5 text-[11px] uppercase tracking-wider ${previewMode === "mobile" ? "bg-gold text-navy" : "bg-white/10 text-white"}`}><Smartphone size={14} /></button>
                <button data-testid="arena-preview-close" onClick={() => setPreview(null)} className="bg-white/10 px-3 py-1.5 text-white"><X size={14} /></button>
              </div>
            </div>
            <iframe
              data-testid="arena-preview-frame"
              title="Anteprima email"
              srcDoc={preview.html}
              style={{ width: previewMode === "desktop" ? 680 : 390, height: "72vh" }}
              className="border-0 bg-white shadow-2xl transition-all"
            />
            <p className="mt-2 text-center text-[11px] text-white/50">Nell'anteprima i link non sono tracciati. Nella email reale ogni pulsante passa dal tracker con i parametri UTM.</p>
          </div>
        </div>
      )}
    </div>
  );
}
